import React, { useState, useEffect, useCallback } from 'react';
import './Hero.css';
import gallery1 from "../../assets/gallery/pt-gallery-1.jpeg";
import gallery2 from "../../assets/gallery/si-gallery-1.jpeg";
import gallery3 from "../../assets/gallery/slider1.jpeg";
import gallery4 from "../../assets/gallery/jg-gallery-2.jpeg";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const ChevronLeft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevronRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.4 1.13 2 2 0 012 1h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
  </svg>
);

const SLIDES = [
  { url: gallery1, label: 'Pumping Test'},
  { url: gallery2, label: 'Soil Investigation Works'},
  { url: gallery3, label: 'TAM Grouting'},
  { url: gallery4, label: 'Jet Grouting'},
];

const STATS = [
  { number: '65',  sup: '+', label: 'Projects Completed' },
  { number: '5',   sup: '+', label: 'Years of Operation' },
  { number: '100', sup: '%', label: 'Completion Rate'    },
  { number: '10',  sup: '+', label: 'Major Clients'      },
];

const Hero = () => {
  const [current, setCurrent]     = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx) => {
    if (animating) return;
    setAnimating(true);
    setCurrent(idx);
    setTimeout(() => setAnimating(false), 600);
  }, [animating]);

  const prev = () => goTo((current - 1 + SLIDES.length) % SLIDES.length);
  const next = useCallback(() => goTo((current + 1) % SLIDES.length), [current, goTo]);

  useEffect(() => {
    const t = setTimeout(next, 4500);
    return () => clearTimeout(t);
  }, [next]);

  return (
    <section className="gsk-hero" id="home">

      <div className="gsk-hero__bg">
        <div className="gsk-hero__bg-gradient" />
        <div className="gsk-hero__bg-grid" />
      </div>
      <div className="gsk-hero__shape gsk-hero__shape--1" />
      <div className="gsk-hero__shape gsk-hero__shape--2" />

      {/* ── Main 2-col content ── */}
      <div className="gsk-hero__content">

        {/* LEFT */}
        <div className="gsk-hero__left">
          <div className="gsk-hero__badge">
            <span className="gsk-hero__badge-dot" />
            <span className="gsk-hero__badge-text">Singapore · Est. 2021 · BizSafe Star Certified</span>
          </div>

          <h1 className="gsk-hero__headline">
            Trusted Partner<br />
            in <em>Geotechnical</em><br />
            Engineering
          </h1>

          <p className="gsk-hero__subheadline">GSK Geotech Pte Ltd</p>

          <p className="gsk-hero__description">
            Specialists in Soil Investigation, Ground Improvement,
            Recharge &amp; Relief Wells and Instrumentation &amp; Monitoring — delivering
            excellence across Singapore's major infrastructure projects since 2021.
          </p>

          <div className="gsk-hero__ctas">
            <a href="projects" className="gsk-hero__btn-primary">
              View Our Projects <ArrowRight />
            </a>
            <a href="tel:+6592398494" className="gsk-hero__btn-secondary">
              <PhoneIcon /> Call Us Now
            </a>
          </div>

          <div className="gsk-hero__certs">
            {['ISO 9001:2015', 'ISO 45001:2018', 'BizSafe Star', 'CR15', 'CR12'].map((c) => (
              <span key={c} className="gsk-hero__cert-tag">{c}</span>
            ))}
          </div>
        </div>

        {/* RIGHT — Image Slider */}
        <div className="gsk-hero__slider">
          <div className="gsk-hero__slides">
            {SLIDES.map((slide, i) => (
              <div key={i} className={`gsk-hero__slide ${i === current ? 'active' : ''}`}>
                <img src={slide.url} alt={slide.label} className="gsk-hero__slide-img" />
                <div className="gsk-hero__slide-overlay" />
                <div className="gsk-hero__slide-caption">
                  <span className="gsk-hero__slide-location">{slide.location}</span>
                  <span className="gsk-hero__slide-label">{slide.label}</span>
                </div>
              </div>
            ))}
          </div>

          <button className="gsk-hero__slider-btn gsk-hero__slider-btn--prev" onClick={prev} aria-label="Previous">
            <ChevronLeft />
          </button>
          <button className="gsk-hero__slider-btn gsk-hero__slider-btn--next" onClick={next} aria-label="Next">
            <ChevronRight />
          </button>

          <div className="gsk-hero__dots">
            {SLIDES.map((_, i) => (
              <button key={i} className={`gsk-hero__dot ${i === current ? 'active' : ''}`} onClick={() => goTo(i)} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>

          <div className="gsk-hero__counter">
            <span className="gsk-hero__counter-current">{String(current + 1).padStart(2, '0')}</span>
            <span className="gsk-hero__counter-sep" />
            <span className="gsk-hero__counter-total">{String(SLIDES.length).padStart(2, '0')}</span>
          </div>

          <div className="gsk-hero__progress">
            <div className="gsk-hero__progress-bar" key={current} />
          </div>
        </div>

      </div>

      {/* ── Stats Bar — full width ── */}
      <div className="gsk-hero__stats-bar">
        {STATS.map((stat, i) => (
          <React.Fragment key={i}>
            <div className="gsk-hero__stat">
              <span className="gsk-hero__stat-number">{stat.number}<sup>{stat.sup}</sup></span>
              <span className="gsk-hero__stat-label">{stat.label}</span>
            </div>
            {i < STATS.length - 1 && <div className="gsk-hero__stat-divider" />}
          </React.Fragment>
        ))}
      </div>

      <a href="#services" className="gsk-hero__scroll" aria-label="Scroll down">
        <span className="gsk-hero__scroll-text">Scroll</span>
        <span className="gsk-hero__scroll-line" />
      </a>

    </section>
  );
};

export default Hero;